import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import SidebarEditor from '@/components/SidebarEditor';
import PreviewPane from '@/components/PreviewPane';

// car-builder-100-funcional, Fase 5: restyle de layout (sidebar colapsable +
// preview grande, paleta clara) sobre el MISMO schema/comportamiento ya
// validado en LAB en Fase 2-4 -- decisión explícita de Alex: NO reescribir a
// un schema `template_config` nuevo (habría exigido migrar la config real de
// GMC/Chevrolet y duplicar `custom_html_path`, que ya vive en `companies`).
// Toda la lógica de red/candados vive aquí; SidebarEditor/PreviewPane son
// puramente presentacionales.

const REVERT_ALL_FIELDS = ['agency_name', 'hero_image', 'logo_url', 'sections', 'fonts', 'text_styles', 'landing_toggles', 'dealer_info', 'promotions', 'favicon_url', 'page_title', 'nav_bg_color', 'footer_text', 'typography_colors'];

export default function CarBuilderPage() {
  const { user } = useAuth();
  // Permisos (writer_user_ids) es la lista de ACL del sitio -- solo un
  // admin/superadmin puede verla o cambiarla (candado real en el backend,
  // ver _rejects_writer_change en routes/car_builder.py; esto es la UI +
  // defensa en profundidad para no mandar el campo si no se puede editar).
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [site, setSite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({ agency_name: '', slug: '', hero_image: '', logo_url: '', writer_user_ids_text: '' });
  const [landingToggles, setLandingToggles] = useState({
    show_modelos: true, show_seminuevos: true, show_refacciones: true, show_mi_auto: true, show_promociones: true,
    show_cotizar: true, show_test_drive: true,
  });
  const [dealerInfo, setDealerInfo] = useState({
    phone: '', address: '', map_lat: null, map_lng: null,
    hours: '', facebook_url: '', instagram_url: '',
    tiktok_url: '', whatsapp_number: '',
  });
  const [liveLanding, setLiveLanding] = useState(null);
  const [liveLandingLoaded, setLiveLandingLoaded] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewWidth, setPreviewWidth] = useState('desktop');
  const [revertingField, setRevertingField] = useState(null);

  const load = async () => {
    setLoadError(null);
    try {
      const { data } = await api.get('/car-builder/site');
      setSite({
        ...data,
        sections: data.sections || {
          home: { title: '', subtitle: '' },
          modelos: { title: 'Nuestros Modelos', subtitle: '' },
          seminuevos: { title: 'Seminuevos Garantizados', subtitle: '' },
          refacciones: { title: 'Refacciones y Accesorios Originales', subtitle: '' },
          mi_auto: { title: 'Mi Auto • Servicio y Mantenimiento', subtitle: '' },
        },
        fonts: Array.isArray(data.fonts) ? data.fonts : [],
        text_styles: data.text_styles || { hero_font: 'inherit', section_font: 'inherit', body_font: 'inherit' },
        promotions: data.promotions || { heading: '', description: '', button_text: 'Ver Promociones', items: [] },
        typography_colors: data.typography_colors || {},
      });
      if (data.landing_toggles && typeof data.landing_toggles === 'object') {
        setLandingToggles((prev) => ({ ...prev, ...data.landing_toggles }));
      }
      if (data.dealer_info && typeof data.dealer_info === 'object') {
        setDealerInfo((prev) => ({ ...prev, ...data.dealer_info }));
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'No se pudo cargar Landing Page Builder';
      setLoadError(msg);
      toast.error(msg);
      return;
    }
    try {
      const wl = await api.get('/white-label/config');
      setLiveLanding(wl.data || null);
    } catch {
      setLiveLanding(null);
    } finally {
      setLiveLandingLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!site) return;
    setWizardData({
      agency_name: site.agency_name || '',
      slug: site.slug || '',
      hero_image: site.hero_image || '',
      logo_url: site.logo_url || '',
      writer_user_ids_text: (site.writer_user_ids || []).join('\n'),
    });
  }, [site]);

  const fontOptions = useMemo(() => ['inherit', ...(site?.fonts || []).map((f) => f.name).filter(Boolean)], [site?.fonts]);

  const hasCustomHtml = !!liveLanding?.custom_html_path;
  const landingEnabled = !!liveLanding?.landing_enabled;

  const loadPreview = async (targetSite) => {
    const s = targetSite || site;
    if (!s?.slug) return;
    setPreviewError(null);
    if (hasCustomHtml) {
      setPreviewHtml(null);
      setPreviewSrc(`${liveLanding.custom_html_path}?_cb_preview=${Date.now()}`);
      return;
    }
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/car-sites/${s.slug}/preview`, { responseType: 'text' });
      setPreviewSrc(null);
      setPreviewHtml(data);
    } catch (e) {
      setPreviewError(e.response?.data?.detail || 'No se pudo cargar la vista previa');
      setPreviewHtml(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (site?.slug && liveLandingLoaded) {
      loadPreview(site);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.slug, liveLandingLoaded, hasCustomHtml]);

  const save = async () => {
    if (!site) return;
    setSaving(true);
    try {
      const payload = {
        agency_name: site.agency_name,
        slug: site.slug,
        hero_image: site.hero_image,
        logo_url: site.logo_url,
        sections: site.sections,
        fonts: site.fonts,
        text_styles: site.text_styles,
        promotions: site.promotions,
        favicon_url: site.favicon_url,
        page_title: site.page_title,
        nav_bg_color: site.nav_bg_color,
        footer_text: site.footer_text,
        typography_colors: site.typography_colors,
        // Solo un admin puede tocar la ACL del sitio -- si no lo es, el campo
        // ni se manda (candado real vive en el backend, esto evita depender
        // solo de que el usuario no vea el input).
        ...(isAdmin ? { writer_user_ids: (site.writer_user_ids || []).filter(Boolean) } : {}),
        landing_toggles: landingToggles,
        dealer_info: dealerInfo,
      };
      const { data } = await api.put('/car-builder/site', payload);
      setSite((prev) => ({ ...prev, ...data }));
      loadPreview({ ...site, ...data });
      toast.success('Configuración guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!site) return;
    setPublishing(true);
    try {
      const { data } = await api.post('/car-builder/publish', {
        agency_name: site.agency_name,
        slug: site.slug,
        hero_image: site.hero_image,
        logo_url: site.logo_url,
        sections: site.sections,
        fonts: site.fonts,
        text_styles: site.text_styles,
        promotions: site.promotions,
        favicon_url: site.favicon_url,
        page_title: site.page_title,
        nav_bg_color: site.nav_bg_color,
        footer_text: site.footer_text,
        typography_colors: site.typography_colors,
        ...(isAdmin ? { writer_user_ids: (site.writer_user_ids || []).filter(Boolean) } : {}),
      });
      toast.success('Sitio publicado');
      const merged = { ...site, is_published: true, slug: data.slug, published_url: data.public_url };
      setSite((prev) => ({ ...prev, ...merged }));
      loadPreview(merged);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo publicar');
    } finally {
      setPublishing(false);
    }
  };

  // Reusa el endpoint genérico de subida de imágenes (ya usado por Propiedades,
  // routes/properties.py::upload_image) -- car-builder no necesita su propio
  // endpoint: sube a disco, devuelve una URL. Nunca se guarda el archivo en
  // el doc de Mongo (sin base64 inline, mismo principio de "un solo dueño del
  // dato" que el resto del módulo).
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.url;
  };

  // Mismo patrón que uploadImage, pero al endpoint dedicado de fuentes
  // (.ttf/.otf/.woff/.woff2 -- /upload/image rechaza esos content-types).
  const uploadFont = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/car-builder/upload-font', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.url;
  };

  const revertField = async (field) => {
    if (hasCustomHtml) return;
    setRevertingField(field);
    try {
      const { data } = await api.delete(`/car-builder/site/${field}`);
      setSite((prev) => ({ ...prev, ...data }));
      if (field === 'landing_toggles' && data.landing_toggles) setLandingToggles(data.landing_toggles);
      if (field === 'dealer_info' && data.dealer_info) setDealerInfo(data.dealer_info);
      loadPreview({ ...site, ...data });
      toast.success('Campo restablecido a su valor por default');
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo restablecer');
      throw e;
    } finally {
      setRevertingField(null);
    }
  };

  // "Restaurar a Defaults" (Fase 5, footer del sidebar): no existe un
  // endpoint de "resetear todo" en el backend -- se reusa el MISMO
  // DELETE /car-builder/site/{field} de la Fase 3, uno por campo conocido.
  // Confirmación nativa a propósito: es un botón rojo que borra TODA la
  // personalización del sitio, no solo un campo -- un click accidental no
  // debe poder disparar esto sin una pausa.
  const resetAll = async () => {
    if (hasCustomHtml || !site) return;
    if (!window.confirm('¿Restablecer TODO Landing Page Builder a sus valores por default? Se pierde toda la personalización actual (no publicada aún no se ve afectada si no has guardado).')) return;
    setResetting(true);
    try {
      let last = null;
      for (const field of REVERT_ALL_FIELDS) {
        // eslint-disable-next-line no-await-in-loop
        last = await revertField(field);
      }
      if (last) loadPreview({ ...site, ...last });
      toast.success('Landing Page Builder restablecido a sus valores por default');
    } catch {
      // revertField ya mostró el toast de error del campo que falló.
    } finally {
      setResetting(false);
    }
  };

  const isLikelyUrl = (v) => !v || /^https?:\/\//i.test(String(v).trim());
  const isLikelyPhone = (v) => !v || /^[0-9+()\s-]{7,20}$/.test(String(v).trim());

  const runWizard = async () => {
    try {
      await api.put('/car-builder/site', {
        agency_name: wizardData.agency_name,
        slug: wizardData.slug,
        hero_image: wizardData.hero_image,
        logo_url: wizardData.logo_url,
        writer_user_ids: wizardData.writer_user_ids_text.split('\n').map((v) => v.trim()).filter(Boolean),
      });
      await api.post('/car-builder/publish', {
        agency_name: wizardData.agency_name,
        slug: wizardData.slug,
        hero_image: wizardData.hero_image,
        logo_url: wizardData.logo_url,
        writer_user_ids: wizardData.writer_user_ids_text.split('\n').map((v) => v.trim()).filter(Boolean),
      });
      setWizardOpen(false);
      setWizardStep(1);
      toast.success('Wizard completado y publicado');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo completar wizard');
    }
  };

  if (loadError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center" data-testid="car-builder-error">
        <p className="text-sm text-red-700 font-medium mb-1">No se pudo cargar Landing Page Builder</p>
        <p className="text-xs text-red-500">{loadError}</p>
      </div>
    );
  }
  if (!site) return <div className="p-6 text-sm text-gray-500" data-testid="car-builder-loading">Cargando Landing Page Builder...</div>;

  const publicHref = landingEnabled
    ? `/l/${site.slug}`
    : (site.is_published ? `/car-sites/${site.slug}` : null);
  // 3 estados reales (nunca se oculta que falta publicar, solo se comunica
  // distinto según cuál es): 'draft' = nunca se publicó nada; 'pending' =
  // ya se publicó antes pero hay un guardado más nuevo sin publicar; 'live'
  // = lo publicado es lo último que se guardó. A pedido explícito de Alexis
  // (2026-09-11): reemplaza el aviso único "⚠️ Cambios sin publicar" que se
  // veía IDÉNTICO antes y después de Guardar.
  const publishStatus = hasCustomHtml
    ? 'live'
    : !site.is_published
      ? 'draft'
      : (site.updated_at && (!site.published_at || site.updated_at > site.published_at))
        ? 'pending'
        : 'live';

  return (
    <div className="space-y-3" data-testid="car-builder-page">
        {(liveLanding?.slug || site.slug) && (
          landingEnabled ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-xs text-green-800 space-y-1" data-testid="car-builder-live-url">
              <p className="font-medium">Landing que ve el cliente</p>
              <p>
                <a className="underline" href={`/l/${liveLanding?.slug || site.slug}`} target="_blank" rel="noreferrer">
                  {typeof window !== 'undefined' ? window.location.origin : ''}/l/{liveLanding?.slug || site.slug}
                </a>
                {liveLanding?.custom_html_path ? ' · HTML propio (fotos y precios salen de este inventario, aunque no pulses Publicar)' : ''}
              </p>
              <p className="text-green-700/70">Publicar genera también /car-sites/{site.slug || 'slug'}. No apagues la URL /l/ — otros tenants ya la comparten.</p>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-1" data-testid="car-builder-live-url">
              <p className="font-medium">Landing pública (/l/) apagada para esta empresa</p>
              <p className="text-amber-700/70">
                Contacta a soporte para activarla. Mientras tanto, publicar genera{' '}
                {site.is_published ? (
                  <a className="underline" href={`/car-sites/${site.slug}`} target="_blank" rel="noreferrer">
                    {typeof window !== 'undefined' ? window.location.origin : ''}/car-sites/{site.slug}
                  </a>
                ) : (
                  <>/car-sites/{site.slug || 'slug'} en cuanto publiques</>
                )}
                .
              </p>
            </div>
          )
        )}

        <div className="flex flex-col lg:flex-row rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm h-[calc(100vh-160px)] min-h-[560px]" data-testid="car-builder-stitch-layout">
          <SidebarEditor
            site={site}
            landingToggles={landingToggles}
            dealerInfo={dealerInfo}
            fontOptions={fontOptions}
            hasCustomHtml={hasCustomHtml}
            isAdmin={isAdmin}
            saving={saving}
            publishing={publishing}
            resetting={resetting}
            revertingField={revertingField}
            publishStatus={publishStatus}
            isLikelyUrl={isLikelyUrl}
            isLikelyPhone={isLikelyPhone}
            onUploadImage={uploadImage}
            onUploadFont={uploadFont}
            onFieldChange={(patch) => setSite((s) => ({ ...s, ...patch }))}
            onSectionChange={(key, patch) => setSite((s) => ({ ...s, sections: { ...(s.sections || {}), [key]: { ...(s.sections?.[key] || {}), ...patch } } }))}
            onToggleChange={(key, val) => setLandingToggles((prev) => ({ ...prev, [key]: val }))}
            onDealerInfoChange={(patch) => setDealerInfo((prev) => ({ ...prev, ...patch }))}
            onPromotionsChange={(patch) => setSite((s) => ({ ...s, promotions: { ...(s.promotions || {}), ...patch } }))}
            onTypographyColorsChange={(patch) => setSite((s) => ({ ...s, typography_colors: { ...(s.typography_colors || {}), ...patch } }))}
            onFontsChange={(fonts) => setSite((s) => ({ ...s, fonts }))}
            onTextStylesChange={(patch) => setSite((s) => ({ ...s, text_styles: { ...(s.text_styles || {}), ...patch } }))}
            onWritersChange={(writer_user_ids) => setSite((s) => ({ ...s, writer_user_ids }))}
            onRevert={revertField}
            onSave={save}
            onPublish={publish}
            onResetAll={resetAll}
          />
          <PreviewPane
            slug={site.slug}
            previewHtml={previewHtml}
            previewSrc={previewSrc}
            previewLoading={previewLoading}
            previewError={previewError}
            previewWidth={previewWidth}
            onWidthChange={setPreviewWidth}
            publicHref={publicHref}
            isPublished={site.is_published}
          />
        </div>

        <div className="flex justify-end">
          <Button variant="outline" size="sm" disabled={hasCustomHtml} onClick={() => { setWizardStep(1); setWizardOpen(true); }} data-testid="car-builder-wizard-open-button">
            ✨ Asistente inicial (3 pasos)
          </Button>
        </div>

        <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
          <DialogContent className="sm:max-w-3xl" data-testid="car-builder-wizard-dialog">
            <DialogHeader>
              <DialogTitle data-testid="car-builder-wizard-title">Wizard Car-Dealer (Paso {wizardStep} de 3)</DialogTitle>
            </DialogHeader>
            {wizardStep === 1 && (
              <div className="grid md:grid-cols-2 gap-3" data-testid="car-builder-wizard-step-1">
                <Input value={wizardData.agency_name} onChange={(e) => setWizardData((s) => ({ ...s, agency_name: e.target.value }))} placeholder="Nombre agencia" data-testid="wizard-agency-name" />
                <Input value={wizardData.slug} onChange={(e) => setWizardData((s) => ({ ...s, slug: e.target.value }))} placeholder="slug" data-testid="wizard-slug" />
                <Input value={wizardData.logo_url} onChange={(e) => setWizardData((s) => ({ ...s, logo_url: e.target.value }))} placeholder="URL logo" data-testid="wizard-logo" />
                <Input value={wizardData.hero_image} onChange={(e) => setWizardData((s) => ({ ...s, hero_image: e.target.value }))} placeholder="URL hero" data-testid="wizard-hero" />
              </div>
            )}
            {wizardStep === 2 && <div className="text-sm text-gray-500" data-testid="car-builder-wizard-step-2">Carga inventario inicial en Car-Dealer DMS para reflejarlo al publicar.</div>}
            {wizardStep === 3 && (
              <div className="space-y-3" data-testid="car-builder-wizard-step-3">
                <p className="text-sm text-gray-500">Permisos y publicación</p>
                <Textarea value={wizardData.writer_user_ids_text} onChange={(e) => setWizardData((s) => ({ ...s, writer_user_ids_text: e.target.value }))} data-testid="wizard-writers" />
              </div>
            )}
            <div className="flex justify-between" data-testid="car-builder-wizard-actions">
              <Button variant="outline" onClick={() => setWizardStep((s) => Math.max(1, s - 1))} disabled={wizardStep === 1} data-testid="wizard-back-button">Anterior</Button>
              {wizardStep < 3 ? (
                <Button onClick={() => setWizardStep((s) => Math.min(3, s + 1))} data-testid="wizard-next-button">Siguiente</Button>
              ) : (
                <Button onClick={runWizard} className="bg-blue-600 hover:bg-blue-700" data-testid="wizard-finish-publish-button">Finalizar y Publicar</Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
}
